import config from "@/config/config";
import type { FileUploadResponse } from "@/core/types";
import { toast } from "@k3-tech/react-kit";

export async function uploadFile(
	file: File | Blob,
	url?: string,
): Promise<FileUploadResponse> {
	if (!config.BACKEND_FILE_ENDPOINT) {
		return Promise.reject(
			new Error('Env var "config.BACKEND_FILE_ENDPOINT" is null!'),
		);
	}

	const uploadUrl =
		url ??
		`${config.BACKEND_FILE_ENDPOINT.replace("/graphql", "")}/api/v1/file/upload`;
	const formData = new FormData();
	const fileName =
		"name" in file && typeof file.name === "string" ? file.name : "file";
	formData.append("file", file, fileName);

	return new Promise((resolve, reject) => {
		fetch(uploadUrl, {
			method: "POST",
			body: formData,
		})
			.then(async (res) => {
				resolve(await res.json());
			})
			.catch((e) => {
				console.error("upload failed", e);
				reject(e);
			});
	});
}

export async function downloadFile(
	endpoint: string,
	options?: Partial<{ method: string; headers: HeadersInit }>,
) {
	if (!config.BACKEND_FILE_ENDPOINT) {
		return Promise.reject(
			new Error('Env var "config.BACKEND_FILE_ENDPOINT" is null!'),
		);
	}

	const downloadUrl = `${config.BACKEND_FILE_ENDPOINT.replace("/graphql", "")}${endpoint ?? "/api/v1/file/download"}`;

	if (!options) {
		window.open(downloadUrl, "_blank");
		return;
	}

	const { method = "GET", headers } = options;
	return fetch(downloadUrl, { method, headers })
		.then(async (res) => {
			if (!res.ok) {
				throw new Error("Network response was not ok");
			}
			const blob = await res.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			const contentDisposition = res.headers.get("Content-Disposition");
			let fileName = "downloaded_file";
			if (contentDisposition) {
				const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
				if (fileNameMatch?.[1]) {
					fileName = fileNameMatch[1];
				}
			}
			a.download = fileName;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		})
		.catch((err) => {
			const msg = "There was a problem during file download";
			console.error(`${msg}:`, err);
			toast.error(msg);
		});
}

export function getFileInfo(_fileID: string) {}

// getFileInlineUrl builds a browser-openable URL that streams the original file
// inline (any mime type, incl. PDFs that imgproxy cannot render). Mirrors the
// masterdata REST file service used by uploadFile/downloadFile.
export function getFileInlineUrl(fileID: string): string {
	const base = config.BACKEND_FILE_ENDPOINT?.replace("/graphql", "") ?? "";
	return `${base}/api/v1/file/download/${fileID}?inline=true`;
}
